import { Button, Card, CardContent, CardHeader, CardTitle, Input, Textarea } from '@ibsys/kit';
import { FC, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type KmEntityType, useKmDomains } from '@/entities/ontology';
import { useFormGuardActions } from '@/entities/formGuard';
import { ConfirmationButton, EditableTitle } from '@/shared/ui';
import { useOntologyForm } from './useOntologyForm';

type Props = {
  initial?: KmEntityType;
  onUnsavedChange?: (hasUnsaved: boolean) => void;
};

/**
 * Форма основной информации типа сущности (создание и редактирование).
 */
export const OntologyForm: FC<Props> = ({ initial, onUnsavedChange }) => {
  const { t } = useTranslation(['common', 'ontology']);
  const navigate = useNavigate();
  const { resetFormGuard } = useFormGuardActions();

  const { form, isEdit, isCreate, isDisabled, isLoading, canAdmin, onSubmit, handleTitleSave, handleDelete } =
    useOntologyForm({ initial });

  const { control, formState } = form;

  const { data: domainsData } = useKmDomains({ includeDeleted: false, limit: 1000 });

  // Домен показываем названием; для домена вне справочника (например, удалённого) — его кодом
  const domainLabel = useMemo(() => {
    if (!initial?.domainCode) {
      return t('ontology:withoutDomain');
    }
    const domain = domainsData?.items.find((item) => item.code === initial.domainCode);
    return domain?.name ?? initial.domainCode;
  }, [initial?.domainCode, domainsData, t]);

  useEffect(() => {
    onUnsavedChange?.(formState.isDirty);
  }, [formState.isDirty, onUnsavedChange]);

  const performDelete = async () => {
    await handleDelete();
    resetFormGuard();
    navigate('/ontology', { replace: true });
  };

  return (
    <Card className="flex flex-col">
      <CardHeader className="border-b flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {isEdit ? (
          <EditableTitle
            value={form.watch('name')}
            label={t('ontology:renameType')}
            onSave={handleTitleSave}
            disabled={isDisabled}
            placeholder={t('ontology:editType')}
            textClassName="text-xl font-semibold"
          />
        ) : (
          <CardTitle>{t('ontology:addType')}</CardTitle>
        )}
        {!isDisabled && (
          <div className="flex gap-2 self-end sm:self-auto">
            {isEdit && canAdmin && (
              <ConfirmationButton
                label={t('ontology:deleteType')}
                onConfirm={performDelete}
                title={t('ontology:removeTypeTitle')}
                description={<Trans>{t('ontology:removeTypeDescription', { name: initial?.name })}</Trans>}
              />
            )}
            <Button isLoading={isLoading} onClick={onSubmit} size="md" variant="outline">
              {t('common:save')}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-2 md:p-6">
        <FormProvider {...form}>
          <div className="flex flex-col gap-4 max-w-3xl">
            {isCreate ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input control={control} name="name" label={t('ontology:name')} required disabled={isDisabled} />
                <Input
                  control={control}
                  name="code"
                  label={t('ontology:code')}
                  required
                  disabled={isDisabled}
                  placeholder={t('ontology:codePlaceholder')}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-muted-foreground">{t('ontology:code')}</span>
                  <span className="font-mono text-sm">{initial?.code}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-muted-foreground">{t('ontology:domain')}</span>
                  <span className="text-sm">{domainLabel}</span>
                </div>
              </div>
            )}

            <Textarea
              control={control}
              name="description"
              label={t('ontology:description')}
              rows={4}
              disabled={isDisabled}
            />
          </div>
        </FormProvider>
      </CardContent>
    </Card>
  );
};
